import { ref } from 'vue';

/**
 * Levelling control centre — saved views (shared).
 *
 * A saved view is a named snapshot of the panel's filter state, persisted per
 * user. The caller supplies `capture()` (→ a plain serialisable filters object)
 * and `apply(filters)`; this composable owns the list, persistence and
 * "is the current filter state this view?" matching via a normalised signature
 * (keys sorted recursively, arrays of primitives sorted) so key/selection order
 * never breaks the match.
 *
 * Persistence is pluggable. By default views live in localStorage (per-user
 * key). Pass async `load`/`save` hooks to back them server-side instead — the
 * beta portal wires these to `authStore.getUserViewSetting/saveUserViewSetting`
 * and rm-portal to `loginState.getUserViewSetting/saveUserViewSetting`, both of
 * which hit the shared `user_view_setting` table. When server hooks are given,
 * localStorage becomes an instant cache (rendered on first paint, then
 * reconciled) and an offline fallback; a first-run migration pushes any
 * pre-existing local views up when the server has no record yet.
 *
 * @param {Object}   options
 * @param {string}   options.storageKey  localStorage key (include the user id)
 * @param {Function} options.capture     () => current filters object
 * @param {Function} options.apply       (filters) => void
 * @param {Function} [options.load]       async () => Array | null   (server read; null = no record)
 * @param {Function} [options.save]       (views) => void|Promise    (server write)
 */
export function levellingSavedViews(options) {
  const { storageKey, capture, apply, load, save } = options;
  const serverBacked = typeof load === 'function' && typeof save === 'function';

  const self = {
    views: ref([]),
    loading: ref(false),
  };

  function _loadLocal() {
    try {
      const raw = localStorage.getItem(storageKey);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (err) {
      return [];
    }
  }

  function _persistLocal() {
    try {
      localStorage.setItem(storageKey, JSON.stringify(self.views.value));
    } catch (err) { /* storage unavailable */ }
  }

  // Local cache first — instant paint for returning users, offline fallback.
  self.views.value = _loadLocal();

  // Server reconciliation: authoritative when a record exists (even if empty);
  // when there's no record yet, migrate any pre-existing local views up once.
  async function _hydrate() {
    if (!serverBacked) return;
    self.loading.value = true;
    try {
      const serverViews = await load();
      if (Array.isArray(serverViews)) {
        self.views.value = serverViews;
        _persistLocal();
      } else if (self.views.value.length) {
        // null = no server record → one-time migration of existing local views.
        await save(self.views.value);
      }
    } catch (err) {
      // Keep the local cache on any failure.
    } finally {
      self.loading.value = false;
    }
  }
  self.ready = _hydrate();

  function _persist() {
    _persistLocal();
    if (serverBacked) {
      try {
        Promise.resolve(save(self.views.value)).catch(() => {});
      } catch (err) { /* ignore */ }
    }
  }

  function _normalise(value) {
    if (Array.isArray(value)) {
      const items = value.map(_normalise);
      // Only sort arrays of primitives — object arrays (e.g. tree selections)
      // keep their order, which is stable for equal selections.
      if (items.every((v) => typeof v !== 'object' || v === null)) return [...items].sort();
      return items;
    }
    if (value && typeof value === 'object') {
      const out = {};
      Object.keys(value).sort().forEach((k) => { out[k] = _normalise(value[k]); });
      return out;
    }
    return value;
  }

  self.signature = (filters) => JSON.stringify(_normalise(filters || {}));

  self.isCurrent = (view) => self.signature(view.filters) === self.signature(capture());

  self.saveCurrent = (name) => {
    const trimmed = String(name || '').trim();
    if (!trimmed) return null;
    const view = { id: 'v' + Date.now(), name: trimmed, filters: capture() };
    self.views.value = [...self.views.value, view];
    _persist();
    return view;
  };

  self.remove = (id) => {
    self.views.value = self.views.value.filter((v) => v.id !== id);
    _persist();
  };

  self.applyView = (view) => {
    if (view && view.filters) apply(view.filters);
  };

  return self;
}
